// @ts-nocheck
import React, { useState, useMemo } from 'react';
import {
  useAssignedEventIds, useComplianceReport, useRoomNightSummary,
  useOutreachEffectiveness, useGroupRateExpiry, useRevenueSummary, useAttritionRisk,
} from './hooks/useReportsData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { RoomBlockPickupReport } from './RoomBlockPickupReport';

function exportCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const HotelReports: React.FC = () => {
  const { assignedEvents, partnerId } = useAssignedEventIds();
  const eventIds = useMemo(() => assignedEvents.map((e) => e.event_id), [assignedEvents]);
  const [eventFilter, setEventFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('compliance');

  const compliance = useComplianceReport(partnerId, eventFilter);
  const roomNights = useRoomNightSummary(eventIds, eventFilter);
  const outreach = useOutreachEffectiveness(eventIds, eventFilter);
  const rateExpiry = useGroupRateExpiry(partnerId, eventFilter);
  const revenue = useRevenueSummary(partnerId, eventFilter);
  const attrition = useAttritionRisk(partnerId, eventIds, eventFilter);

  const tabs = [
    { key: 'compliance', label: 'Compliance' },
    { key: 'room-nights', label: 'Room Nights' },
    { key: 'outreach', label: 'Outreach' },
    { key: 'rate-expiry', label: 'Rate Expiry' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'attrition', label: 'Attrition Risk' },
    { key: 'block-pickup', label: 'Room Block Pickup' },
  ];

  const handleExport = () => {
    const filterSuffix = eventFilter !== 'all' ? `-${eventFilter}` : '';
    switch (activeTab) {
      case 'compliance': {
        const d = compliance.data || [];
        exportCsv(`compliance${filterSuffix}.csv`,
          ['Event', 'Total Teams', 'Confirmed', 'Exempt', 'Unbooked', 'Compliance %'],
          d.map((r: any) => [r.event_name, r.total_teams, r.confirmed_bookings, r.exempt_bookings, r.unbooked_teams, `${r.compliance_pct}%`]));
        break;
      }
      case 'room-nights': {
        const d = roomNights.data || [];
        exportCsv(`room-nights${filterSuffix}.csv`,
          ['Event', 'Hotel', 'Room Nights', 'Rooms'],
          d.map((r: any) => [r.events?.name, r.hotel_name, r.room_nights, r.number_of_rooms]));
        break;
      }
      case 'outreach': {
        const d = outreach.data || [];
        exportCsv(`outreach${filterSuffix}.csv`,
          ['Event', 'Emails Sent', 'Bookings Within 7d', 'Conversion %'],
          d.map((r: any) => [r.event_name, r.emails_sent, r.bookings_within_7d, `${r.conversion_rate}%`]));
        break;
      }
      case 'rate-expiry': {
        const d = rateExpiry.data || [];
        exportCsv(`rate-expiry${filterSuffix}.csv`,
          ['Event', 'Expiry Date', 'Days Remaining', 'Unbooked Teams'],
          d.map((r: any) => [r.event_name, r.group_rate_expiry, r.days_remaining, r.unbooked_teams]));
        break;
      }
      case 'revenue': {
        const d = revenue.data || [];
        exportCsv(`revenue${filterSuffix}.csv`,
          ['Event', 'Hotel', 'Room Nights', 'Gross Revenue', 'Commission', 'Rebate'],
          d.map((r: any) => [r.events?.name, r.hotel_properties?.hotel_name, r.total_room_nights, r.gross_revenue, r.total_commission, r.total_rebate]));
        break;
      }
      case 'attrition': {
        const d = attrition.data || [];
        exportCsv(`attrition${filterSuffix}.csv`,
          ['Event', 'Hotel', 'Contracted', 'Booked', 'Pickup %', 'Threshold %', 'Rooms at Risk', 'Release Date'],
          d.map((r: any) => [r.event_name, r.hotel_name, r.rooms_contracted, r.rooms_booked, `${r.pickup_pct}%`, `${r.attrition_threshold}%`, r.rooms_at_risk, r.rooms_released_date]));
        break;
      }
    }
  };

  const renderSkeleton = () => (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Reports</h1>
        <div className="flex items-center gap-2">
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {assignedEvents.map((ev) => (
                <SelectItem key={ev.event_id} value={ev.event_id}>{ev.event_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activeTab !== 'block-pickup' && <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="block-pickup"><RoomBlockPickupReport events={assignedEvents} eventFilter={eventFilter} /></TabsContent>

        {/* 1. Compliance */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader><CardTitle className="text-base">Compliance by Event</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {compliance.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Confirmed</TableHead>
                      <TableHead>Exempt</TableHead>
                      <TableHead>Unbooked</TableHead>
                      <TableHead>Compliance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(compliance.data || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r.event_name}</TableCell>
                        <TableCell>{r.total_teams}</TableCell>
                        <TableCell>{r.confirmed_bookings}</TableCell>
                        <TableCell>{r.exempt_bookings}</TableCell>
                        <TableCell className="text-red-600 font-medium">{r.unbooked_teams}</TableCell>
                        <TableCell>
                          <Badge variant={r.compliance_pct >= 80 ? 'default' : 'destructive'}>
                            {r.compliance_pct}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. Room Nights */}
        <TabsContent value="room-nights">
          <Card>
            <CardHeader><CardTitle className="text-base">Room Night Summary</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {roomNights.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Room Nights</TableHead>
                      <TableHead>Rooms</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(roomNights.data || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.events?.name}</TableCell>
                        <TableCell>{r.hotel_name}</TableCell>
                        <TableCell className="font-medium">{r.room_nights}</TableCell>
                        <TableCell>{r.number_of_rooms}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. Outreach */}
        <TabsContent value="outreach">
          <Card>
            <CardHeader><CardTitle className="text-base">Outreach Effectiveness</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {outreach.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Emails Sent</TableHead>
                      <TableHead>Booked Within 7d</TableHead>
                      <TableHead>Conversion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(outreach.data || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.event_name}</TableCell>
                        <TableCell>{r.emails_sent}</TableCell>
                        <TableCell>{r.bookings_within_7d}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{r.conversion_rate}%</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4. Rate Expiry */}
        <TabsContent value="rate-expiry">
          <Card>
            <CardHeader><CardTitle className="text-base">Group Rate Expiry Watch</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {rateExpiry.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Days Left</TableHead>
                      <TableHead>Unbooked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(rateExpiry.data || []).map((r: any, i: number) => (
                      <TableRow key={i} className={r.days_remaining <= 7 ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                        <TableCell className="font-medium">{r.event_name}</TableCell>
                        <TableCell>{r.group_rate_expiry ? format(parseISO(r.group_rate_expiry), 'MMM d, yyyy') : '—'}</TableCell>
                        <TableCell>
                          <Badge variant={r.days_remaining <= 7 ? 'destructive' : 'secondary'}>
                            {r.days_remaining}d
                          </Badge>
                        </TableCell>
                        <TableCell>{r.unbooked_teams}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Revenue */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader><CardTitle className="text-base">Revenue & Commission</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {revenue.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Room Nights</TableHead>
                      <TableHead>Gross Revenue</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Rebate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(revenue.data || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.events?.name}</TableCell>
                        <TableCell>{r.hotel_properties?.hotel_name}</TableCell>
                        <TableCell>{r.total_room_nights}</TableCell>
                        <TableCell className="font-medium">${Number(r.gross_revenue || 0).toLocaleString()}</TableCell>
                        <TableCell>${Number(r.total_commission || 0).toLocaleString()}</TableCell>
                        <TableCell>${Number(r.total_rebate || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 6. Attrition */}
        <TabsContent value="attrition">
          <Card>
            <CardHeader><CardTitle className="text-base">Attrition Risk</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {attrition.isLoading ? <div className="p-4">{renderSkeleton()}</div> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Hotel</TableHead>
                      <TableHead>Contracted</TableHead>
                      <TableHead>Booked</TableHead>
                      <TableHead>Pickup</TableHead>
                      <TableHead>Threshold</TableHead>
                      <TableHead>At Risk</TableHead>
                      <TableHead>Release Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(attrition.data || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.event_name}</TableCell>
                        <TableCell>{r.hotel_name}</TableCell>
                        <TableCell>{r.rooms_contracted}</TableCell>
                        <TableCell>{r.rooms_booked}</TableCell>
                        <TableCell>{r.pickup_pct}%</TableCell>
                        <TableCell>{r.attrition_threshold}%</TableCell>
                        <TableCell className="text-red-600 font-medium">{r.rooms_at_risk}</TableCell>
                        <TableCell>
                          {r.rooms_released_date ? format(parseISO(r.rooms_released_date), 'MMM d') : '—'}
                          {r.release_days != null && r.release_days <= 14 && (
                            <Badge variant="destructive" className="ml-1 text-xs">{r.release_days}d</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HotelReports;
